import type { BulkUpdateSkipReason } from '@shared/featureFlagEvaluation';
import type { FeatureFlagId } from '@shared/featureFlags';

import { fetchWithConfig } from './apiUtils';

interface FeatureFlagEntry {
  id: string;
  enabled: boolean;
}

interface FeatureFlagsResponse {
  features?: FeatureFlagEntry[];
  [key: string]: unknown;
}

/** Per-user features payload for `POST /bulk-update`. */
export interface BulkUpdate {
  userId: string;
  features: Partial<Record<FeatureFlagId, boolean>>;
}

export interface BulkUpdateResultRow {
  userId: string;
  applied: Array<{ featureId: string; enabled: boolean }>;
  skipped: Array<{ featureId: string; reason: BulkUpdateSkipReason }>;
}

interface BulkUpdateResponse {
  success: boolean;
  results: BulkUpdateResultRow[];
  [key: string]: unknown;
}

export const featureFlagsApi = {
  getAllFeatureFlags: async (token?: string): Promise<FeatureFlagsResponse> => {
    try {
      const response = await fetchWithConfig<FeatureFlagsResponse>(
        '/feature-flags',
        {
          authToken: token,
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching all feature flags:', error);
      throw error;
    }
  },

  getMyFeatureFlags: async (token: string): Promise<FeatureFlagsResponse> => {
    try {
      const response = await fetchWithConfig<FeatureFlagsResponse>(
        '/feature-flags/my-features',
        {
          authToken: token,
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching user feature flags:', error);
      throw error;
    }
  },

  getUserFeatureFlags: async (
    userId: string,
    token: string
  ): Promise<FeatureFlagsResponse> => {
    try {
      const response = await fetchWithConfig<FeatureFlagsResponse>(
        `/feature-flags/user/${userId}`,
        {
          authToken: token,
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching user feature flags:', error);
      throw error;
    }
  },

  toggleUserFeatureFlag: async (
    userId: string,
    featureId: string,
    enabled: boolean,
    token: string
  ): Promise<FeatureFlagsResponse> => {
    try {
      const response = await fetchWithConfig<FeatureFlagsResponse>(
        `/feature-flags/user/${userId}/toggle`,
        {
          method: 'POST',
          authToken: token,
          body: JSON.stringify({ featureId, enabled }),
        }
      );
      return response;
    } catch (error) {
      console.error('Error toggling user feature flag:', error);
      throw error;
    }
  },

  removeUserFeatureFlag: async (
    userId: string,
    featureId: string,
    token: string
  ): Promise<FeatureFlagsResponse> => {
    try {
      const response = await fetchWithConfig<FeatureFlagsResponse>(
        `/feature-flags/user/${userId}/${featureId}`,
        {
          method: 'DELETE',
          authToken: token,
        }
      );
      return response;
    } catch (error) {
      console.error('Error removing user feature flag:', error);
      throw error;
    }
  },

  bulkUpdateFeatureFlags: async (
    updates: BulkUpdate[],
    token?: string
  ): Promise<BulkUpdateResponse> => {
    try {
      const response = await fetchWithConfig<BulkUpdateResponse>(
        '/feature-flags/bulk-update',
        {
          method: 'POST',
          authToken: token,
          body: JSON.stringify({ updates }),
        }
      );
      return response;
    } catch (error) {
      console.error('Error bulk updating feature flags:', error);
      throw error;
    }
  },
};
